import SERVICES_API_V1 from 'api/services/v1'
import EmptyResults from 'components/EmptyResults'
import GridSpinner from 'components/UI/GridSpinner'
import { Button, Grid, Icon } from 'gu-ui-kit'
import React, { useEffect, useState } from 'react'
import { Link, useHistory, useRouteMatch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import styles from 'modules/Services/Access/List/network-account-access.module.scss'
import Table from './subcomponents/Table'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import filterPanelConfigs from 'modules/Services/Access/List/subcomponents/filtersConfigs'
import FilterPanel from 'components/Filters/FilterPanel'
import { ROUTES } from 'constants/routes'
import {
  ACCESS_DROP_LIST,
  ACCESS_FETCH,
  ACCESS_SET_FILTER,
  FETCH_NETWORK_ACCOUNT,
} from 'modules/Services/types'
import PageHeader from 'components/PageHeader'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import Breadcrumbs from 'components/Breadcrumbs'

const AccessList = () => {
  const pathGen = useGeneratePathI18n()
  const breakpoint = useBreakpoint()
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation([])
  const [actionsAvailable, setActionsAvailable] = useState(false)
  const [agencyExternalId, setAgencyExternalId] = useState(null)
  const {
    params: { networkType, accountId },
  } = useRouteMatch()

  const { i18n, filters, deleteQueue, data, loading, networkAccount } = useSelector(
    (state) => ({
      i18n: state['app'].i18n,
      filters: state['services/account-access'].filters,
      deleteQueue: state['services/account-access'].deleteQueue,
      data: state['services/account-access'].data,
      loading: state['services/account-access'].loading,
      networkAccount: state['services/network-account'],
    }),
    shallowEqual,
  )

  const [page, setPage] = useState(1)

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['services/account-access'].filters),
    config: filterPanelConfigs[networkType](),
    setAction: ACCESS_SET_FILTER,
  })

  useEffect(async () => {
    return Promise.all([
      SERVICES_API_V1.getAccessSettings({
        networkType,
        accountId,
      }),
      new Promise((resolve, reject) =>
        dispatch({
          type: FETCH_NETWORK_ACCOUNT,
          payload: { accountId },
          meta: { resolve, reject },
        }),
      ),
    ])
      .then(([access_settings, account]) => {
        const available = !!access_settings.response?.data?.actions_available
        const externalId = account?.data?.external_id

        if (available && externalId) {
          setActionsAvailable(available)
          setAgencyExternalId(externalId)
        } else {
          throw new Error('agency external id error')
        }
      })
      .catch(() => {
        history.push(
          pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT, {
            networkType,
            accountId,
          }),
        )
      })
  }, [])

  useEffect(() => {
    dispatch({ type: ACCESS_DROP_LIST })
    setPage(1)
  }, [filterManager.params])

  useEffect(() => {
    if (!agencyExternalId) {
      return
    }

    const requestPayload =
      networkType !== 'vk-ads'
        ? {
            networkType,
            accountId,
            params: filterManager.params,
          }
        : {
            networkType,
            params: {
              ...filterManager.params,
              agency_client_external_id: agencyExternalId,
            },
          }

    dispatch({
      type: ACCESS_FETCH,
      payload: requestPayload,
    })
  }, [filterManager.params, page, agencyExternalId])

  return (
    <>
      <Breadcrumbs
        links={[
          {
            title: t('services::heading'),
            to: pathGen(ROUTES.SERVICES),
          },
          {
            title: `${t(`services::networks.${networkType}`)}${
              networkType.match(/high-risk$/) ? ' (High Risk)' : ''
            }`,
            to: pathGen(ROUTES.SERVICES_NETWORK, {
              networkType,
            }),
          },
        ]}
      />

      <Grid
        direction={'column'}
        className={'margin-bottom-40'}
      >
        <PageHeader className={'margin-bottom-24'}>
          {t('services::accesses.header', {
            account_name: networkAccount.name ?? networkAccount.login,
          })}
        </PageHeader>
        {actionsAvailable && (
          <div>
            <Button
              component={Link}
              to={pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT_ACCESS_NEW, {
                networkType,
                accountId,
              })}
              size={'l'}
              variant={'primary'}
              fullWidth={!breakpoint.md}
              startIcon={<Icon name={'plus'} />}
            >
              {t('services::accesses.add_new_access')}
            </Button>
          </div>
        )}
      </Grid>

      <FilterPanel
        filterManager={filterManager}
        className={'margin-bottom-32'}
      />

      {data.length === 0 && loading && (
        <div className={styles['accesses-list__spinner-container']}>
          <GridSpinner
            width={32}
            style={{ height: '100%' }}
          />
        </div>
      )}

      {!loading && data.length === 0 ? (
        <EmptyResults text={t('services::accesses.empty_list.heading')} />
      ) : (
        data.length !== 0 &&
        filters && (
          <Table
            accountId={accountId}
            networkType={networkType}
            processingIds={deleteQueue.processingIds}
            data={data}
            filters={filters}
            i18n={i18n}
          />
        )
      )}
    </>
  )
}

AccessList.propTypes = {}

export default AccessList
